/**
 * Makes a falsy check to see if a value exists.
 * It's a type guard. If the value exists, it returns true.
 *
 * If you want to allow zero or empty string, you can use the `existsWithOptions` curried function instead
 *
 * @template T
 * @param {(T | undefined)} value
 * @return {*}  {value is NonNullable<T>}
 */
function exists<T>(value: T | undefined): value is NonNullable<T> {
    return !!value;
}

/**
 * Checks if a value exists. If you want to allow zero or empty string, you can use this function.
 * It's a type guard. If the value exists, it returns true.
 *
 * Set allowZero to true if you want to allow the number 0
 * Set allowEmptyString to true if you want to allow the empty string
 *
 * Null and undefined are always considered as not existing.
 */
const existsWithOptions =
    (
        options: {
            allowZero?: boolean;
            allowEmptyString?: boolean;
        } = {
            allowZero: false,
            allowEmptyString: false,
        },
    ) =>
    <T>(value: T): value is NonNullable<T> => {
        if (!options || (!options.allowZero && !options.allowEmptyString)) {
            // We'll do a simple falsy check
            return !!value;
        }
        if (value === null) return false;
        if (typeof value === "undefined") return false;
        if (typeof value === "number" && !options.allowZero) return value !== 0;
        if (typeof value === "string" && !options.allowEmptyString)
            return value !== "";
        return true;
    };

export { exists, existsWithOptions };
